import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '@/core/api/queries';
import { ticketsApi, type TicketTierDto, type TicketRedemptionStatusUpdateDto } from '../api/tickets.api';
import { getErrorMessage } from '@/core/api/response-handler';

/**
 * Mutatiile de tichete (spec §8).
 *
 * Toate invalideaza INTREGUL router `tickets`, nu doar lista atinsa: o revendicare schimba si
 * soldul, si istoricul, si starea nivelurilor (maxPerSchool). O invalidare punctuala ar lasa
 * butonul „Revendica" activ dupa ce soldul a scazut sub cost.
 */
export function useTicketMutations() {
    const queryClient = useQueryClient();
    const invalidateAll = () => queryClient.invalidateQueries({ queryKey: api.tickets.getKey() });

    /**
     * Fara toast de succes, INTENTIONAT: confirmarea revendicarii e un ECRAN (R12), randat de
     * pagina. Un toast peste ecranul de confirmare ar spune acelasi lucru de doua ori, iar pe
     * mobil ar acoperi exact cifra soldului ramas.
     */
    const redeem = useMutation({
        mutationFn: ({ tierId, alegereProfesor }: { tierId: string; alegereProfesor?: string }) =>
            ticketsApi.redeem(tierId, alegereProfesor),
        onSuccess: invalidateAll,
        onError: (error) => toast.error(getErrorMessage(error, 'Premiul nu a putut fi solicitat')),
    });

    const createTier = useMutation({
        mutationFn: (dto: TicketTierDto) => ticketsApi.createTier(dto),
        onSuccess: () => { toast.success('Nivel creat'); invalidateAll(); },
        onError: (error) => toast.error(getErrorMessage(error, 'Eroare la crearea nivelului')),
    });

    const updateTier = useMutation({
        mutationFn: (dto: TicketTierDto) => ticketsApi.updateTier(dto),
        onSuccess: () => { toast.success('Nivel actualizat'); invalidateAll(); },
        onError: (error) => toast.error(getErrorMessage(error, 'Eroare la actualizarea nivelului')),
    });

    const deleteTier = useMutation({
        mutationFn: (id: string) => ticketsApi.deleteTier(id),
        onSuccess: () => { toast.success('Nivel șters'); invalidateAll(); },
        onError: (error) => toast.error(getErrorMessage(error, 'Nivelul nu poate fi șters — are revendicări')),
    });

    /**
     * ⚠️ La CANCELLED, `body.note` e OBLIGATORIU - backendul refuza cu 400 (`ticket.note.required`).
     * Motivul e singura explicatie pe care o primeste scoala: punctele revin in sold implicit,
     * fara rand de credit si fara istorie de stari.
     */
    const updateStatus = useMutation({
        mutationFn: ({ id, body }: { id: string; body: TicketRedemptionStatusUpdateDto }) =>
            ticketsApi.updateStatus(id, body),
        onSuccess: () => { toast.success('Stare actualizată'); invalidateAll(); },
        onError: (error) => toast.error(getErrorMessage(error, 'Eroare la actualizarea stării')),
    });

    return { redeem, createTier, updateTier, deleteTier, updateStatus };
}
